import { spring, interpolate } from 'remotion';
import { THEME, getScale, getLayoutMode } from '../../themes/brand';
import { getIcon } from './iconMap';
import { Layers } from 'lucide-react';
import type { BrollComponentProps } from './registry';

interface ListItem {
  text: string;
  icon?: string;
}

interface ListProps {
  title?: string;
  icon?: string;
  items?: (string | ListItem)[];
  numbered?: boolean;
  accentIndex?: number;
}

// Left-border accent color cycle (matches leadgen app pattern)
const BORDER_ACCENTS = [
  THEME.colors.accent,
  THEME.colors.blue,
  THEME.colors.purple,
  THEME.colors.orange,
  THEME.colors.success,
  THEME.colors.warning,
];

export const TemplateList: React.FC<BrollComponentProps> = ({
  frame, fps, width, height, durationInFrames, props,
}) => {
  const s = getScale(height);
  const mode = getLayoutMode(height);
  const data = (props || {}) as ListProps;
  const dur = durationInFrames;

  const title = data.title || 'Key Points';
  const TitleIcon = getIcon(data.icon) || Layers;
  const rawItems = data.items || ['Item 1', 'Item 2', 'Item 3'];
  const items: ListItem[] = rawItems.map(it => typeof it === 'string' ? { text: it } : it);
  const numbered = data.numbered !== false;
  const accentIndex = data.accentIndex ?? -1;

  const maxItems = mode === 'compact' ? 5 : 8;
  const visibleItems = items.slice(0, maxItems);
  const staggerGap = Math.max(2, Math.floor((dur * 0.5) / visibleItems.length));
  const staggerBase = Math.round(dur * 0.1);

  const titleEntrance = spring({ frame, fps, config: { damping: 16 }, durationInFrames: Math.max(10, Math.round(dur * 0.15)) });

  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      backgroundColor: THEME.colors.bg, fontFamily: THEME.font.family,
      padding: THEME.spacing.xl * s,
    }}>
      {/* Title with icon-in-pill */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: THEME.spacing.md * s,
        marginBottom: THEME.spacing.xl * s,
        opacity: titleEntrance,
        transform: `translateY(${interpolate(titleEntrance, [0, 1], [20, 0])}px)`,
      }}>
        <div style={{
          width: 44 * s, height: 44 * s,
          backgroundColor: THEME.colors.bgSecondary,
          borderRadius: THEME.radius.md * s,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${THEME.colors.border}`,
        }}>
          <TitleIcon size={22 * s} color={THEME.colors.accent} />
        </div>
        <div style={{
          color: THEME.colors.text, fontSize: THEME.font.title * s,
          fontWeight: THEME.font.weight.bold, letterSpacing: 2 * s,
        }}>
          {title}
        </div>
      </div>

      {/* List items */}
      <div style={{ width: '100%', maxWidth: 900 * s, display: 'flex', flexDirection: 'column', gap: THEME.spacing.md * s }}>
        {visibleItems.map((item, i) => {
          const delay = staggerBase + i * staggerGap;
          const entrance = spring({
            frame: Math.max(0, frame - delay), fps,
            config: { damping: 14, stiffness: 100 },
          });

          const isAccent = i === accentIndex;
          const borderColor = isAccent ? THEME.colors.accent : BORDER_ACCENTS[i % BORDER_ACCENTS.length];
          const ItemIcon = getIcon(item.icon);

          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: THEME.spacing.md * s,
              padding: `${THEME.spacing.md * s}px ${THEME.spacing.lg * s}px`,
              backgroundColor: isAccent ? THEME.colors.bgSecondary : THEME.colors.bgCard,
              borderRadius: THEME.radius.md * s,
              borderLeft: `${Math.max(3, 4 * s)}px solid ${borderColor}`,
              border: `1px solid ${THEME.colors.border}`,
              borderLeftWidth: Math.max(3, 4 * s),
              borderLeftColor: borderColor,
              opacity: entrance,
              transform: `translateX(${interpolate(entrance, [0, 1], [60, 0])}px)`,
            }}>
              {/* Number badge or icon-in-pill */}
              {ItemIcon ? (
                <div style={{
                  width: 32 * s, height: 32 * s,
                  backgroundColor: THEME.colors.bgSecondary,
                  borderRadius: THEME.radius.sm * s,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${THEME.colors.border}`,
                  flexShrink: 0,
                }}>
                  <ItemIcon size={16 * s} color={borderColor} />
                </div>
              ) : numbered ? (
                <div style={{
                  width: 32 * s, height: 32 * s,
                  backgroundColor: isAccent ? THEME.colors.accent : THEME.colors.bgSecondary,
                  borderRadius: THEME.radius.sm * s,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${THEME.colors.border}`,
                  color: isAccent ? THEME.colors.bg : THEME.colors.textMuted,
                  fontSize: THEME.font.label * s, fontWeight: THEME.font.weight.bold,
                  flexShrink: 0,
                }}>
                  {i + 1}
                </div>
              ) : null}

              <div style={{
                color: isAccent ? THEME.colors.accent : THEME.colors.text,
                fontSize: THEME.font.body * s * 1.1,
                fontWeight: isAccent ? THEME.font.weight.semibold : THEME.font.weight.normal,
              }}>
                {item.text}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
